// Due-date/blocked urgency bucketing for the "My Day" dashboard view.
import { differenceInCalendarDays, startOfDay } from 'date-fns'
import { Issue } from '../types'

export type UrgencyBucketKey = 'overdue' | 'dueToday' | 'blocked' | 'dueSoon'

export interface UrgencyBuckets {
  overdue: Issue[]
  dueToday: Issue[]
  blocked: Issue[]
  dueSoon: Issue[]
  missingEstimate: Issue[]
}

export interface UrgencyOptions {
  now?: Date
  dueSoonDays?: number
}

const DEFAULT_DUE_SOON_DAYS = 5

// Statuses considered "shipped/finished" for My Day purposes - excluded from
// urgency buckets and active-work stat counts. Includes legacy 'done' for
// projects still on the old workflow, and 'live'/'qa_live' since shipped work
// doesn't need daily attention (mirrors the DONE_STATUS_CATEGORIES convention
// used for the Sprint progress calculation before the Sprint feature was
// removed).
const DONE_STATUS_CATEGORIES = ['completed', 'done', 'live', 'qa_live']

export const isIssueDone = (issue: Issue): boolean =>
  DONE_STATUS_CATEGORIES.includes(issue.status?.category)

// Orthogonal to the urgency buckets below - an in-progress issue with no
// estimate can also be overdue/blocked/etc. at the same time, so this is
// computed independently rather than as another urgency bucket outcome.
export const needsEstimate = (issue: Issue): boolean =>
  issue.status?.category === 'in_progress' && !issue.timeTracking?.originalEstimate

// Shared across every UI entry point that can trigger the in-progress/estimate
// gate (Kanban/MyBoard drag-and-drop, the Issue Detail Modal's status
// dropdown) so the wording can't drift out of sync between them. The backend
// has its own copy (ESTIMATE_REQUIRED_MESSAGE in routes/issues.js) since it's
// a separate runtime - keep the two in sync by hand if this ever changes.
export const ESTIMATE_REQUIRED_MESSAGE = 'Set a time estimate before moving this issue to In Progress.'

export const getIssueUrgencyBucket = (
  issue: Issue,
  options: UrgencyOptions = {}
): UrgencyBucketKey | null => {
  if (isIssueDone(issue)) return null

  if (!issue.dueDate) {
    return issue.blocked ? 'blocked' : null
  }

  const dueDate = new Date(issue.dueDate)
  if (isNaN(dueDate.getTime())) {
    return issue.blocked ? 'blocked' : null
  }

  const now = options.now ?? new Date()
  const dueSoonDays = options.dueSoonDays ?? DEFAULT_DUE_SOON_DAYS
  const dayDiff = differenceInCalendarDays(startOfDay(dueDate), startOfDay(now))

  if (dayDiff < 0) return 'overdue'
  if (dayDiff === 0) return 'dueToday'
  if (issue.blocked) return 'blocked'
  if (dayDiff <= dueSoonDays) return 'dueSoon'
  return null
}

const dueDateTime = (issue: Issue): number =>
  issue.dueDate ? new Date(issue.dueDate).getTime() : Infinity

const sortByDueDateAscending = (issues: Issue[]): Issue[] =>
  [...issues].sort((a, b) => dueDateTime(a) - dueDateTime(b))

const sortBlockedBucket = (issues: Issue[]): Issue[] =>
  [...issues].sort((a, b) => {
    const dueDiff = dueDateTime(a) - dueDateTime(b)
    if (dueDiff !== 0) return dueDiff

    const priorityDiff = (b.priority?.level ?? 0) - (a.priority?.level ?? 0)
    if (priorityDiff !== 0) return priorityDiff

    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  })

export const bucketIssuesByUrgency = (
  issues: Issue[],
  options: UrgencyOptions = {}
): UrgencyBuckets => {
  const buckets: UrgencyBuckets = {
    overdue: [],
    dueToday: [],
    blocked: [],
    dueSoon: [],
    missingEstimate: [],
  }

  issues.forEach((issue) => {
    const bucket = getIssueUrgencyBucket(issue, options)
    if (bucket) buckets[bucket].push(issue)
    if (needsEstimate(issue)) buckets.missingEstimate.push(issue)
  })

  return {
    overdue: sortByDueDateAscending(buckets.overdue),
    dueToday: sortByDueDateAscending(buckets.dueToday),
    blocked: sortBlockedBucket(buckets.blocked),
    dueSoon: sortByDueDateAscending(buckets.dueSoon),
    missingEstimate: [...buckets.missingEstimate].sort((a, b) => (a.title || '').localeCompare(b.title || '')),
  }
}
